import React from 'react';

import { useDispatch } from 'react-redux';

import { Checkbox } from '@atlaskit/checkbox';
import {
  PopupSelect,
  components,
  OptionProps,
  ValueType,
  MenuListComponentProps,
  OptionType,
} from '@atlaskit/select';
import { gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'src/hooks/intl';
import { changeFilterState } from 'src/redux/jira/actions';
import { UNASSIGNED_USER_ID } from 'src/redux/jira/constants';
import { AssigneeType, UserOption } from 'src/redux/jira/types';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { SCREEN_NAME } from '../../constants';

import * as styles from './assignees-filter.styled';
import messages from './assignees-filters.18n';

const DROPDOWN_SIZE_LIMIT = 25;

type AssigneesFilterDropdownProps = {
  options: UserOption[];
  value: UserOption[];
};

const IconOption = (props: OptionProps) => {
  return (
    <components.Option {...props}>
      <styles.OptionContentContainer>
        <Checkbox isChecked={props.isSelected} />
        {props.data.avatar}
        <styles.OptionLabelContainer>
          {props.data.label}
        </styles.OptionLabelContainer>
      </styles.OptionContentContainer>
    </components.Option>
  );
};

const assigneeClickedEvent = (assigneeType: AssigneeType) =>
  publishUiEvent({
    action: 'clicked',
    actionSubject: 'assignee',
    actionSubjectId: 'assigneeFilter',
    source: SCREEN_NAME,
    attributes: {
      assigneeType,
    },
  });

export const AssigneesFilterDropdown = (
  props: AssigneesFilterDropdownProps
) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { options, value } = props;

  const MenuList = (menuListProps: MenuListComponentProps<OptionType>) => {
    const { children } = menuListProps;
    const numChildren = Array.isArray(children) ? children.length : 0;
    const truncatedResults = Array.isArray(children)
      ? children.slice(0, DROPDOWN_SIZE_LIMIT)
      : [];
    const excessAssignees = numChildren - truncatedResults.length;

    return (
      <components.MenuList {...menuListProps}>
        {truncatedResults}
        {excessAssignees > 0 && (
          <styles.MoreResultsLabelContainer>
            <div>
              {intl.formatMessage(messages.moreAssignees, {
                numAssignees: excessAssignees,
              })}
            </div>
            <div>{intl.formatMessage(messages.typeToRefineListFurther)}</div>
          </styles.MoreResultsLabelContainer>
        )}
        {numChildren === 0 && (
          <styles.MoreResultsLabelContainer>
            {intl.formatMessage(messages.noMatchingOptions)}
          </styles.MoreResultsLabelContainer>
        )}
      </components.MenuList>
    );
  };

  return (
    <PopupSelect
      options={options}
      value={value}
      target={({ ref }) => (
        <styles.AvatarBorder isSelected={false} ref={ref}>
          <styles.MoreAssigneesTrigger spacing="compact">
            {options.length > 99 ? `${options.length}` : `+${options.length}`}
          </styles.MoreAssigneesTrigger>
        </styles.AvatarBorder>
      )}
      className="multi-select"
      classNamePrefix="react-select"
      isMulti
      popperProps={{ placement: 'bottom-start' }}
      searchThreshold={10}
      placeholder={intl.formatMessage(messages.filterAssignees)}
      closeMenuOnSelect={false}
      components={{ Option: IconOption, MenuList }}
      styles={{
        option: base => ({
          ...base,
          color: token('color.text'),
          backgroundColor: token('color.background.input'),
          '&:hover': {
            backgroundColor: token('elevation.surface.raised.hovered'),
          },
        }),
        container: base => ({
          ...base,
          width: gridSize() * 33,
        }),
      }}
      hideSelectedOptions={false}
      onChange={(newSelectedUsers: ValueType<UserOption>) => {
        if (Array.isArray(newSelectedUsers)) {
          const addedUsers = newSelectedUsers.filter(
            (user: UserOption) => !value.includes(user)
          );

          if (addedUsers.length !== 0) {
            let assigneeType: AssigneeType;
            if (addedUsers[0] === UNASSIGNED_USER_ID) {
              assigneeType = AssigneeType.Unassigned;
            } else {
              assigneeType = AssigneeType.Dropdown;
            }
            assigneeClickedEvent(assigneeType);
          }

          dispatch(
            changeFilterState({
              assignees: newSelectedUsers.map((user: UserOption) => user.value),
            })
          );
        }
      }}
    />
  );
};
